import { RoomType, type Floor, type PostBuildingRoomsRequestBody } from "@eisox/api/core-api";
import { z } from "@eisox/zod";

import { TEMPERATURE_MAX, TEMPERATURE_MIN } from "~/constants/temperature.constant";
import type { AvailableRoomTypes } from "~/helpers/room.helper";
import { m } from "~/paraglide/messages";

export interface withProperties {
  uid: number | null;
  id: string;
}

/** Resolves the bucket key used in globalRooms for a given room form data.
 * Returns the same key used by handleAddRoom / handleUpdateRoom writes. */
export const resolveRoomKey = (floors: Floor[], object: Object): string =>
  object.pattern ? checkGetName(floors, object, false) : "default";

export const schemaErrors = <T extends withProperties>(
  roomsName: string[],
  nextUid: number,
  fields: Record<string, T[] | undefined>,
  plans: Floor[],
) =>
  z
    .object({
      id: z.string(),
      floor: z.object({
        id: z.string(),
        x: z.number(),
        y: z.number(),
      }),
      groupNames: z.array(z.string()),
      pattern: z.string(),
      name: z.string(),
      uid: z
        .number()
        .min(nextUid, m.number_should_be_greater_than_or_equal_to_min_number({ n: nextUid }))
        .nullable(),
      type: z.enum(RoomType),
      comfortTemperature: z.number().min(TEMPERATURE_MIN).max(TEMPERATURE_MAX),
      nightTemperature: z.number().min(TEMPERATURE_MIN).max(TEMPERATURE_MAX),
      isSwitchEnabled: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.uid ? roomsName.filter(r => r !== data.name).includes(data.name) : roomsName.includes(data.name)) {
        ctx.addIssue({
          code: "custom",
          error: m.room_name_should_be_unique(),
          path: ["name"],
        });
      }
    })
    .superRefine((data, ctx) => {
      if (data.pattern.includes("(index)") && data.uid == null) {
        ctx.addIssue({
          code: "custom",
          error: m.empty_field_forbidden(),
          path: ["uid"],
        });
      }
    })
    .superRefine((data, ctx) => {
      if (
        data.uid &&
        data.pattern &&
        fields.length &&
        !Object.values(fields)
          .flat()
          .find(f => f?.id === data.id) &&
        data.uid <=
          Math.max(
            ...(fields[resolveRoomKey(plans, data)]?.map(r => r.uid).filter((uid): uid is number => uid != null) ?? [
              0,
            ]),
          )
      ) {
        ctx.addIssue({
          code: "custom",
          error: m.number_should_be_less_than_or_equal_to_max_number({ n: nextUid }),
          path: ["uid"],
        });
      }
    })
    .superRefine((data, ctx) => {
      if (
        data.type === RoomType.ROOM &&
        ((data.nightTemperature && data.nightTemperature > data.comfortTemperature) || !data.nightTemperature)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["nightTemperature", "comfortTemperature"],
        });
      }
    });

export type RoomFormDto = z.infer<ReturnType<typeof schemaErrors>>;
export type RoomsDto = Record<string, RoomFormDto[]>;

export const roomFormToCreateBody = ({
  floor,
  groupNames,
  name,
  type,
  comfortTemperature,
  nightTemperature,
  isSwitchEnabled,
}: RoomFormDto): PostBuildingRoomsRequestBody[number] => ({
  name,
  type,
  comfortTemperature,
  nightTemperature,
  isSwitchEnabled,
  floorId: floor.id,
  x: floor.x,
  y: floor.y,
  ...(groupNames.length > 0 ? { group: groupNames } : {}),
});

type Object = Omit<RoomFormDto, "floor"> & { floor: { id: string } };

type SpecialFunctions<T> = {
  [K in keyof T]?: (value: T[K]) => string | number;
};

type translateKeys<T> = { [K in keyof T]?: string };

export const prefixId = "room";

const formatUid = (uid: number) => uid.toString().padStart(2, "0");

const replaceCheckPatterns = <T extends Record<string, any>>(
  object: T,
  pattern: string,
  replaceIndex = true,
  specialFunctions?: SpecialFunctions<T>,
  translateKeys?: translateKeys<T>,
) => {
  const regex = /\((.*?)\)/g;
  const name = pattern.replace(regex, (_, key) => {
    if (key === "index" && !replaceIndex) return key;
    const newKey = translateKeys ? Object.keys(translateKeys).find(k => translateKeys[k] === key) || key : key;
    const value = object[newKey];
    return specialFunctions?.[newKey]?.(value) ?? value ?? key;
  });
  return name;
};

export const checkGetName = (floors: Floor[], object: Object, replaceIndex = true, patternToCheck?: string) => {
  if (!object.pattern) return object.name;
  const customFunction: SpecialFunctions<Object> = {
    floor: floor => floors.find(f => f.id === floor.id)?.name || "-",
    type: type => m.room_type_abbreviation({ type: type as (typeof AvailableRoomTypes)[number] }),
    uid: uid => (uid ? formatUid(uid) : ""),
  };
  const translateKeys: translateKeys<Object> = {
    floor: m.level().toLowerCase(),
    type: m.type().toLowerCase(),
    uid: m.index().toLowerCase(),
  };
  return replaceCheckPatterns(object, patternToCheck || object.pattern, replaceIndex, customFunction, translateKeys);
};
